import { api } from '../api';
import {
  AdminUnregisterPlayerDto,
  CreateBlindStructureDto,
  CreatePayoutBracketDto,
  CreatePayoutStructureDto,
  CreateTournamentTemplateData,
  TableWithPlayers,
  TournamentBlindStructure,
  TournamentEvent,
  TournamentInfoDto,
  TournamentPayoutBracket,
  TournamentPayoutStructure,
  TournamentPayoutTransactionDto,
  TournamentRecurringType,
  TournamentTemplateDto,
  TournamentTemplateWComputed,
  TournamentUpdateInput,
  UpdateBlindStructureDto,
  UpdateTournamentTemplateData,
} from '@repo/common-types';
import { formatUrl } from '@repo/common-types/src/utils/url-utils';

export const fundPrizePoolRest = async (tournamentId: string, amount: number) => {
  const response = await api.post(`/v1/admin/tournaments/${tournamentId}/fund-prize-pool`, { amount });
  return response.data;
};

export const extractFundsRest = async (tournamentId: string, amount: number) => {
  const response = await api.post(`/v1/admin/tournaments/${tournamentId}/extract-funds`, { amount });
  return response.data;
};

export const closeRegistrationRest = async (tournamentUrl: string) => {
  if (!tournamentUrl) {
    throw new Error('Tournament server URL is required to close registration');
  }

  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/close-registration`);
  return response.data;
};

export const completeTournamentRest = async (tournamentId: string) => {
  const response = await api.post(`/v1/admin/tournaments/${tournamentId}/complete`);
  return response.data;
};

export const movePlayerRest = async (tournamentUrl: string, playerId: string, tableId: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/move-player`, { playerId, tableId });
  return response.data;
};

export const eliminatePlayerRest = async (tournamentUrl: string, playerId: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/eliminate-player`, { playerId });
  return response.data;
};

export const syncPlayerRest = async (tournamentUrl: string, playerId: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/sync-player`, { playerId });
  return response.data;
};

export const getTournamentBlindsStructureRest = async (name?: string): Promise<CreateBlindStructureDto[]> => {
  const response = await api.get<CreateBlindStructureDto[]>(
    `/v1/admin/tournaments/blind-structures`,
    name ? { params: name } : undefined,
  );
  return response.data;
};

export const createTournamentBlindsStructureRest = async (data: CreateBlindStructureDto) => {
  const response = await api.post<TournamentBlindStructure>(`/v1/admin/tournaments/blind-structures`, data);
  return response.data;
};

export const updateTournamentFullBlindStructure = async (data: UpdateBlindStructureDto) => {
  const response = await api.patch<TournamentBlindStructure>(`/v1/admin/tournaments/blind-structures/${data.id}`, data);
  return response.data;
};

export const getTournamentTemplatesRest = async (sng?: boolean) => {
  const response = await api.get<TournamentTemplateDto[]>(`/v1/admin/tournaments/templates${sng ? '?sng=true' : ''}`);
  return response.data;
};

export const createTournamentTemplateRest = async (data: CreateTournamentTemplateData) => {
  const response = await api.post<TournamentTemplateWComputed>(`/v1/admin/tournaments/templates`, data);
  return response.data;
};

export const updateTournamentTemplateRest = async (data: UpdateTournamentTemplateData & { id: string }) => {
  const { id, ...payload } = data;
  const response = await api.patch<TournamentTemplateWComputed>(`/v1/admin/tournaments/templates/${data.id}`, payload);
  return response.data;
};

export const getPayoutStructuresRest = async () => {
  const response = await api.get<TournamentPayoutStructure[]>(`/v1/admin/tournaments/payout-structures`);
  return response.data;
};

export const getBracketsByStructureIdRest = async (structureId: string) => {
  const response = await api.get<TournamentPayoutBracket[]>(
    `/v1/admin/tournaments/payout-structures/${structureId}/brackets`,
  );
  return response.data;
};

export const createPayoutStructureRest = async (data: CreatePayoutStructureDto) => {
  const response = await api.post(`/v1/admin/tournaments/payout-structures`, data);
  return response.data;
};

export const validateBracketsRest = async (data: CreatePayoutBracketDto[]) => {
  const response = await api.post(`/v1/admin/tournaments/payout-structures/validate-brackets`, data);
  return response.data;
};

export const updatePayoutStructureRest = async (data: { id: string; name: string }) => {
  const response = await api.patch(`/v1/admin/tournaments/payout-structures/${data.id}`, data);
  return response.data;
};

export const deletePayoutStructureRest = async (id: string) => {
  const response = await api.delete(`/v1/admin/tournaments/payout-structures/${id}`);
  return response.data;
};

export const duplicatePayoutStructureRest = async (id: string) => {
  const response = await api.post(`/v1/admin/tournaments/payout-structures/${id}/duplicate`);
  return response.data;
};

export const createOrUpdateRangePayoutBracketRest = async (
  payoutStructureId: string,
  data: CreatePayoutBracketDto[],
) => {
  const response = await api.post(`/v1/admin/tournaments/payout-structures/${payoutStructureId}/brackets`, data);
  return response.data;
};

export const editPayoutStructureRest = async (data: any) => {
  const response = await api.patch(`/v1/admin/tournaments/payout-structures/full/${data.id}`, data);
  return response.data;
};

export const getTournamentsRest = async ({
  sng,
  includeArchived,
  skip = 0,
  take = 10,
}: {
  sng?: boolean;
  includeArchived?: boolean;
  skip?: number;
  take?: number;
}) => {
  const response = await api.get<{ data: TournamentInfoDto[]; total: number; skip: number; take: number }>(
    `/v1/admin/tournaments?sng=${sng ? 'true' : 'false'}&${includeArchived ? 'includeArchived=true' : ''}&skip=${skip}&take=${take}`,
  );
  return response.data;
};

export const deleteTournamentRest = async (tournamentId: string) => {
  const response = await api.delete(`/v1/admin/tournaments/${tournamentId}`);
  return response.data;
};

export const createTournamentRest = async (
  templateId: string,
  startTime?: Date,
  recurring?: boolean,
  recurringType?: TournamentRecurringType,
) => {
  const response = await api.post(`/v1/admin/tournaments/templates/${templateId}`, {
    startTime: startTime ? new Date(startTime).toISOString() : undefined,
    recurring,
    recurringType,
  });
  return response.data;
};

export const getTournamentPlayersRest = async (tournamentId: string, search?: string) => {
  const response = await api.get(`/v1/admin/tournaments/${tournamentId}/players?search=${search}`);
  return response.data;
};

export const getTournamentPayoutTransactionsRest = async (tournamentId: string) => {
  const response = await api.get<TournamentPayoutTransactionDto[]>(
    `/v1/admin/tournaments/${tournamentId}/payout-transactions`,
  );
  return response.data;
};

export const getTournamentOverviewStatsRest = async (fromDate?: string, toDate?: string, sng?: boolean) => {
  const response = await api.get(
    `/v1/admin/tournaments/overview?from=${fromDate}&to=${toDate}${sng ? '&sng=true' : ''}`,
  );
  return response.data;
};

export const getTournamentDetailsRest = async (tournamentId: string) => {
  const response = await api.get<TournamentInfoDto>(`/v1/admin/tournaments/${tournamentId}`);
  return response.data;
};

export const pauseTournamentRest = async (tournamentUrl: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/pause`);
  return response.data;
};

export const resumeTournamentRest = async (tournamentUrl: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/resume`);
  return response.data;
};

export const getTournamentTablesRest = async (tournamentId: string) => {
  const response = await api.get<TableWithPlayers[]>(`/v1/admin/tournaments/${tournamentId}/tables`);
  return response.data;
};

export const getTournamentEventsRest = async (tournamentId: string) => {
  const response = await api.get<TournamentEvent[]>(`/v1/admin/tournaments/${tournamentId}/events`);
  return response.data;
};

export const getTournamentRegistrationsAdminRest = async (
  tournamentId: string,
): Promise<{
  registrations: Array<{
    id: string;
    userId: string;
    isEliminated: boolean;
    eliminationAt: string | null;
    finalPosition: number | null;
    prizeAwarded: number;
    reentriesCount: number;
    user: { id: string; alias: string; countryCode: string | null };
    isInTable: boolean;
    stack: number;
  }>;
  total: number;
}> => {
  const response = await api.get(`/v1/admin/tournaments/${tournamentId}/registrations`);
  return response.data;
};

export const updateTournamentRegistrationAdminRest = async (
  tournamentId: string,
  registrationId: string,
  data: { isEliminated?: boolean; finalPosition?: number | null; prizeAwarded?: number | null },
) => {
  const response = await api.patch(`/v1/admin/tournaments/${tournamentId}/registrations/${registrationId}`, data);
  return response.data;
};

export const deleteTournamentRegistrationAdminRest = async (tournamentId: string, registrationId: string) => {
  const response = await api.delete(`/v1/admin/tournaments/${tournamentId}/registrations/${registrationId}`);
  return response.data;
};

export const updateTournamentRest = async (
  tournamentId: string | null | undefined,
  tournamentUpdateDto: Partial<TournamentUpdateInput>,
): Promise<TournamentInfoDto> => {
  const response = await api.patch<TournamentInfoDto>(`/v1/admin/tournaments/${tournamentId}`, {
    ...tournamentUpdateDto,
  });
  return response.data;
};

export const cancelTournamentRest = async (tournamentUrl: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/cancel`);
  return response.data;
};

export const cancelTournamentRestLobby = async (tournamentId: string) => {
  const response = await api.post(`/v1/admin/tournaments/${tournamentId}/cancel`);
  return response.data;
};

export const restartTournamentRest = async (tournamentId: string) => {
  const response = await api.post(`/v1/admin/tournaments/${tournamentId}/restart`);
  return response.data;
};

export const enableHandByHandRest = async (tournamentUrl: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/hand-by-hand/enable`);
  return response.data;
};

export const disableHandByHandRest = async (tournamentUrl: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/hand-by-hand/disable`);
  return response.data;
};

export const bulkRegisterPlayersRest = async (tournamentId: string, count: number) => {
  const response = await api.post(`/v1/admin/tournaments/bulk-register`, {
    tournamentId,
    count,
  });
  return response.data;
};

export const getBulkRegistrationStatus = async (transactionIds: string[]) => {
  const response = await api.post(`/v1/admin/tournaments/bulk-register/status`, {
    transactionIds,
  });
  return response.data;
};

export const getTournamentTotalChips = async (tournamentId: string) => {
  const response = await api.get(`/v1/admin/tournaments/${tournamentId}/total-chips`);
  return response.data;
};

export const startHandsRest = async (tournamentUrl: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/start-hands`);
  return response.data;
};

export const requestRebalanceRest = async (tournamentUrl: string) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/request-rebalance`);
  return response.data;
};

export const deleteTableServerRest = async (tableId: string) => {
  const response = await api.delete(`/v1/admin/table/${tableId}/server`);
  return response.data;
};

export const getTableStatesRest = async (tournamentUrl: string) => {
  const response = await api.post<{
    success: boolean;
    tables: Array<{
      tableId: string;
      lastHandCompletedAt: Date | null;
      playerCount: number;
      isActive: boolean;
      handsDealt: number;
    }>;
    message?: string;
  }>(`${formatUrl(tournamentUrl)}/admin/tournaments/table-states`);
  return response.data;
};

export const adminUnregisterPlayerRest = async (tournamentId: string, userId: string) => {
  const response = await api.post<void>(`/v1/admin/tournaments/${tournamentId}/admin-unregister`, {
    userId,
  } satisfies AdminUnregisterPlayerDto);
  return response.data;
};

// Best-effort: toggle table active state in tournament-server cache (if running)
export const setTournamentServerTableActiveRest = async (tournamentUrl: string, tableId: string, isActive: boolean) => {
  const response = await api.post(`${formatUrl(tournamentUrl)}/admin/tournaments/tables/${tableId}/active`, {
    isActive,
  });
  return response.data;
};

// Toggle tournament table assignment active state in main server (DB)
export const setTournamentTableActiveRest = async (tournamentId: string, tableId: string, active: boolean) => {
  const response = await api.post(`/v1/admin/tournaments/${tournamentId}/tables/${tableId}/active`, { active });
  return response.data;
};
